import { db } from '../firebase';
import { 
  collection, 
  addDoc, 
  getDocs, 
  query, 
  where, 
  serverTimestamp, 
  orderBy, 
  updateDoc, 
  doc,
  Timestamp
} from 'firebase/firestore';
import { handleFirestoreError, OperationType } from '../lib/firestoreError';

export interface Message {
  id?: string;
  tenantId: string;
  senderUid: string;
  senderName: string;
  senderRole: string;
  recipientUid?: string | null; // Null for broadcasts
  subject: string;
  body: string;
  type: 'Announcement' | 'Inquiry' | 'Feedback' | 'Support';
  status: 'Unread' | 'Read' | 'Archived';
  timestamp: Timestamp | any;
}

export const sendMessage = async (tenantId: string, message: Omit<Message, 'id' | 'timestamp'>) => {
  const path = `tenants/${tenantId}/messages`;
  try {
    const messagesRef = collection(db, 'tenants', tenantId, 'messages');
    await addDoc(messagesRef, {
      ...message,
      timestamp: serverTimestamp()
    });
  } catch (error) {
    handleFirestoreError(error, OperationType.CREATE, path);
  }
};

export const getMessagesForUser = async (tenantId: string, userUid: string) => {
  const path = `tenants/${tenantId}/messages`;
  const messagesRef = collection(db, 'tenants', tenantId, 'messages');
  
  try {
    const qSent = query(messagesRef, where('senderUid', '==', userUid), orderBy('timestamp', 'desc'));
    const qReceived = query(messagesRef, where('recipientUid', '==', userUid), orderBy('timestamp', 'desc'));
    const qBroadcast = query(messagesRef, where('recipientUid', '==', null), orderBy('timestamp', 'desc'));

    console.log('DEBUG: qSent', qSent, 'userUid:', userUid);
    console.log('DEBUG: qReceived', qReceived, 'userUid:', userUid);
    console.log('DEBUG: qBroadcast', qBroadcast);

    const [sentSnap, receivedSnap, broadcastSnap] = await Promise.allSettled([
      getDocs(qSent),
      getDocs(qReceived),
      getDocs(qBroadcast)
    ]);

    const allMessages: Message[] = [];
    
    if (sentSnap.status === 'fulfilled') {
      allMessages.push(...sentSnap.value.docs.map(d => ({ id: d.id, ...d.data() } as Message)));
    } else {
      console.error('DEBUG: Error in qSent', sentSnap.reason);
    }
    
    if (receivedSnap.status === 'fulfilled') {
      allMessages.push(...receivedSnap.value.docs.map(d => ({ id: d.id, ...d.data() } as Message)));
    } else {
      console.error('DEBUG: Error in qReceived', receivedSnap.reason);
    }
    
    if (broadcastSnap.status === 'fulfilled') {
      allMessages.push(...broadcastSnap.value.docs.map(d => ({ id: d.id, ...d.data() } as Message)));
    } else {
      console.error('DEBUG: Error in qBroadcast', broadcastSnap.reason);
    }

    console.log('DEBUG: getMessagesForUser found:', allMessages.length, 'messages for user:', userUid);
    
    // De-duplicate and sort
    const uniqueMessages = Array.from(new Map(allMessages.map(m => [m.id, m])).values());
    console.log('DEBUG: Final unique messages count:', uniqueMessages.length);
    
    return uniqueMessages.sort((a, b) => (b.timestamp?.toMillis() || 0) - (a.timestamp?.toMillis() || 0));
  } catch (error) {
    handleFirestoreError(error, OperationType.LIST, path);
  }
};

export const getManagerInbox = async (tenantId: string) => {
  const path = `tenants/${tenantId}/messages`;
  try {
    const messagesRef = collection(db, 'tenants', tenantId, 'messages');
    // Managers see broadcasts and all inquiries/feedback
    const q = query(messagesRef, orderBy('timestamp', 'desc'));
    const snap = await getDocs(q);
    const messages = snap.docs.map(d => ({ id: d.id, ...d.data() } as Message));
    console.log('DEBUG: getManagerInbox found:', messages.length, 'messages');
    return messages;
  } catch (error) {
    handleFirestoreError(error, OperationType.LIST, path);
  }
};

export const getUnreadMessageCount = async (tenantId: string, userUid: string) => {
  const path = `tenants/${tenantId}/messages`;
  try {
    const messagesRef = collection(db, 'tenants', tenantId, 'messages');
    
    // Unread messages for the user as recipient
    const q = query(
      messagesRef, 
      where('recipientUid', '==', userUid),
      where('status', '==', 'Unread')
    );
    
    const snap = await getDocs(q);
    return snap.size;
  } catch (error) {
    handleFirestoreError(error, OperationType.LIST, path);
    return 0;
  }
};

export const markMessageRead = async (tenantId: string, messageId: string) => {
  const path = `tenants/${tenantId}/messages/${messageId}`;
  try {
    const messageRef = doc(db, 'tenants', tenantId, 'messages', messageId);
    await updateDoc(messageRef, { status: 'Read' });
  } catch (error) {
    handleFirestoreError(error, OperationType.UPDATE, path);
  }
};
